import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from "../../../environments/environment";
import {LogErrorCommand} from "homey-housing-lottery-app-dto";
import {UserService} from './user.service';
import {catchError, of} from "rxjs";
import botd from '@fingerprintjs/botd';
import {PlatformService} from "./platform.service";
import {isNative} from "../utils";
import {Capacitor} from "@capacitor/core";
import {App} from "@capacitor/app";

let loadedBotd: Awaited<ReturnType<typeof botd.load>> | undefined;

@Injectable({
  providedIn: 'root'
})
export class ClientErrorLoggingService {
  private readonly httpClient = inject(HttpClient);
  private readonly userService = inject(UserService);
  private readonly platformService = inject(PlatformService);
  private readonly oldOnError = console.error;
  private appVersion: string | undefined;

  // Rate limiting: max 10 errors per 5 minutes
  private errorTimestamps: number[] = [];
  private readonly MAX_ERRORS = 10;
  private readonly WINDOW_MS = 5 * 60 * 1000; // 5 minutes

  private stringifyExcludingCycles(error: unknown): string {
    const cache: any[] = [];
    const retVal = JSON.stringify(
      error,
      (key, value) =>
        typeof value === "object" && value !== null
          ? cache.includes(value)
            ? undefined // Duplicate reference found, discard key
            : cache.push(value) && value // Store value in our collection
          : value,
      2
    );
    return retVal;
  }

  public initialize(): void {
    if (this.platformService.isServer) {
      return;
    }

    if (isNative) {
      App.getInfo().then(info => {
        this.appVersion = info.version;
      }).catch(err => this.oldOnError('Failed to retrieve app version', err));
    }

    console.error = (...args: any[]) => {
      const now = Date.now();
      // Prune timestamps older than WINDOW_MS
      this.errorTimestamps = this.errorTimestamps.filter(ts => now - ts < this.WINDOW_MS);

      // If we've reached the limit, skip logging
      if (this.errorTimestamps.length >= this.MAX_ERRORS) {
        this.oldOnError('Error rate limit reached, skipping log');
        return;
      }

      // Record this error timestamp
      this.errorTimestamps.push(now);

      // Call the original console.error
      this.oldOnError(...args);

      const user = this.userService.user();
      const lastUserId = this.userService.lastUserId();
      let userId: string | undefined;

      if (user !== undefined && user.status !== "unauthorized") {
        userId = user.id;
      }

      const lines = args.map(
        (arg) => {
          if (arg instanceof Error) {
            return arg.message + "\n" + arg.stack;
          } else if (typeof arg === "string") {
            return arg;
          } else {
            return "Pure object: " + this.stringifyExcludingCycles(arg);
          }
        }
      ).join("\n");

      if (!window.location.href.includes('localhost') || isNative) {
        const command: Omit<LogErrorCommand, 'bot'> = {
          platform: Capacitor.getPlatform(),
          userId,
          lastUserId,
          pageUrl: window.location.href,
          screenResolution: `${screen.width}x${screen.height}`,
          windowSize: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          referrer: document.referrer,
          userAgent: navigator.userAgent,
          message: lines,
          appVersion: isNative ? this.appVersion : undefined,
        }

        this.sendErrorLog(command);
      }
    }
  }

  public async sendErrorLog(command: Omit<LogErrorCommand, 'bot'>) {
    if (!loadedBotd) {
      loadedBotd = await botd.load();
    }

    const result = loadedBotd.detect();

    const finalCommand: LogErrorCommand = {
      ...command,
      bot: result.bot,
    }

    this.httpClient.post(`${environment.apiUrl}/api/public/logs`, finalCommand)
      .pipe(catchError((error) => {
        this.oldOnError('Error logging to API:', error);
        return of(null);
      }))
      .subscribe();
  }
}
